import { createContext, useContext, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { Expand, GripVertical, Info, X } from 'lucide-react'
import { IconButton, StatePanel, Tooltip } from '../common'
import { apiErrorMessage } from '../../utils/apiErrorMessage'
import { interactiveAncestorWithin } from '../../utils/interactiveElements'
import styles from './WidgetFrame.module.css'
import { WidgetAvailability } from './widgetAvailability'

// Where a widget's own control lands. Putting it on the title line costs a
// list one fewer row of chrome and gives it one more row of data. In edit
// mode the header belongs to the layout, so the control is suppressed.
const HeaderSlot = createContext(null)

export function WidgetControls({ children }) {
  const slot = useContext(HeaderSlot)
  if (!slot?.node) return null
  return createPortal(children, slot.node)
}

// The metric definition behind the ⓘ. Portaled so it is never clipped by the
// widget's own overflow, and it replaces the browser's native tooltip rather
// than doubling it.
export function MetricInfo({ text, label }) {
  return (
    <Tooltip content={text} placement="bottom">
      <button type="button" className={styles.info} aria-label={label} title="">
        <Info size={14} aria-hidden="true" />
      </button>
    </Tooltip>
  )
}

// The chrome every widget shares: a title, the metric source behind ⓘ, the
// expand shortcut, and in edit mode a drag handle and remove. Widgets render
// only their content; nothing about position lives here.
export function WidgetFrame({
  title,
  source,
  definition,
  editable = false,
  dragHandleProps,
  onRemove,
  onExpand,
  actions,
  children,
  className = '',
}) {
  const [available, setAvailable] = useState(true)
  const [headerSlot, setHeaderSlot] = useState(null)
  const slot = useMemo(() => (editable ? null : { node: headerSlot }), [editable, headerSlot])
  const resolvedTitle = title || definition?.title
  const resolvedSource = source || definition?.source
  const expandable = Boolean(onExpand)

  const expandFromCard = (event) => {
    if (!expandable || editable) return
    // A menu or tooltip opened from this card renders in a portal: the click
    // still bubbles here through React's tree, but it landed outside the
    // card and belongs to that control. Picking an ordering is not a request
    // to expand the widget.
    if (!event.currentTarget.contains(event.target)) return
    // Among the clicks that did land on the card, a control keeps its own.
    // The match has to be inside the card too: the dashboard sits in a
    // focusable tab panel, so an unbounded closest() would find that and
    // treat every click as a control's.
    const control = interactiveAncestorWithin(event.target, event.currentTarget)
    // Read-only chart rows can take keyboard focus for inspection while a
    // pointer press still opens the card. Nested buttons keep their actions.
    if (control && !control.hasAttribute('data-chart-inspection')) return
    onExpand()
  }

  // In edit mode the whole header picks the widget up, which is a far
  // bigger target than the grip. Its buttons keep their own clicks; the grip
  // is the one control that is also a way to start a drag.
  const dragFromHeader = editable && dragHandleProps?.onPointerDown
    ? (event) => {
      if (interactiveAncestorWithin(event.target, event.currentTarget) && !event.target.closest('[data-grip]')) return
      dragHandleProps.onPointerDown(event)
    }
    : undefined

  return (
    <section
      className={`${styles.frame} ${editable ? styles.editable : ''} ${expandable && !editable ? styles.expandable : ''} ${className}`.trim()}
      aria-label={resolvedTitle}
      hidden={!available && !editable}
      onClick={expandFromCard}
    >
      <header className={styles.header} onPointerDown={dragFromHeader}>
        {editable ? (
          // The focusable handle: keyboard moves start here, and it is the
          // visible cue that the card can be picked up. Its pointer presses
          // reach the header's handler.
          <button
            type="button"
            className={styles.handle}
            data-grip=""
            aria-label={`Move ${resolvedTitle}. Use arrow keys to move, Shift and arrow keys to resize.`}
            onKeyDown={dragHandleProps?.onKeyDown}
          >
            <GripVertical size={18} aria-hidden="true" />
          </button>
        ) : null}
        <h3 className={styles.title}>
          {resolvedTitle}
          {resolvedSource && !editable ? <MetricInfo text={resolvedSource} label={`How ${resolvedTitle} is measured`} /> : null}
        </h3>
        {editable ? null : <div className={styles.controls} ref={setHeaderSlot} />}
        <div className={styles.actions}>
          {editable ? null : actions}
          {expandable ? (
            <IconButton
              className={styles.expand}
              variant="ghost"
              label={`Expand ${resolvedTitle}`}
              onClick={onExpand}
            >
              <Expand size={16} aria-hidden="true" />
            </IconButton>
          ) : null}
          {editable && onRemove ? (
            <IconButton variant="ghost" label={`Remove ${resolvedTitle}`} onClick={onRemove}>
              <X size={16} aria-hidden="true" />
            </IconButton>
          ) : null}
        </div>
      </header>
      {!available && editable ? <div className={styles.body}>Not available</div> : null}
      <div className={styles.body} hidden={!available}>
        <HeaderSlot.Provider value={slot}>
          <WidgetAvailability.Provider value={setAvailable}>{children}</WidgetAvailability.Provider>
        </HeaderSlot.Provider>
      </div>
    </section>
  )
}

// Loading, error and empty states shared by widget bodies.
export function WidgetState({ loading, error, empty, emptyTitle = 'Nothing in this range yet', emptyDescription, onRetry, children }) {
  if (loading) return <StatePanel state="loading" title="Loading" compact />
  if (error) {
    return <StatePanel state="error" title="Could not load" description={apiErrorMessage(error)} onRetry={onRetry} compact />
  }
  if (empty) return <StatePanel state="empty" title={emptyTitle} description={emptyDescription} compact />
  return children
}

export function UnknownWidget({ type }) {
  return (
    <StatePanel
      state="disabled"
      title="This widget is not available"
      description={`"${type}" is not part of this Studio version. Remove it or update Studio.`}
      compact
    />
  )
}
